import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Shirt } from "lucide-react";
import FeedCard from "@/components/FeedCard";
import type { KitWithPhoto } from "@/hooks/use-home-feed";

interface Props {
  loading: boolean;
  feedKits: KitWithPhoto[];
  hasMore: boolean;
  loadingMore: boolean;
  loadMoreFeed: () => void;
  pageSize: number;
  loadError?: boolean;
  isRetrying?: boolean;
  retry?: () => void;
}


const FeedSkeleton = () => (
  <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
    <div className="flex items-center gap-2.5 px-4 py-3">
      <Skeleton className="h-8 w-8 rounded-full" />
      <Skeleton className="h-4 w-24" />
    </div>
    <Skeleton className="aspect-[4/3] w-full" />
    <div className="p-4 space-y-2">
      <Skeleton className="h-5 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  </div>
);

export default function HomeFeed({ loading, feedKits, hasMore, loadingMore, loadMoreFeed, pageSize, loadError, isRetrying, retry }: Props) {
  return (
    <section className="bg-secondary pt-6 pb-12">
      <div className="container">
        <div className="mb-6">
          <h2 className="text-2xl font-black tracking-tight text-foreground md:text-3xl">Co nowego u kolekcjonerów?</h2>
        </div>

        <div className="mx-auto max-w-[1100px]">
          {loading ? (
            <div className="grid grid-cols-2 gap-3 sm:gap-5">
              {Array.from({ length: 4 }).map((_, i) => <FeedSkeleton key={i} />)}
            </div>
          ) : feedKits.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-3 sm:gap-5">
                {feedKits.map((kit) => (
                  <FeedCard
                    key={kit.id}
                    id={kit.id}
                    club={kit.club}
                    playerName={(kit as any).player_name}
                    season={kit.season}
                    brand={kit.brand}
                    kitType={kit.kit_type}
                    condition={kit.condition}
                    photoUrl={kit.photoUrl}
                    likesCount={kit.likesCount}
                    commentsCount={kit.commentsCount}
                    ownerName={kit.ownerName}
                    ownerAvatar={kit.ownerAvatar}
                    ownerUserId={kit.user_id}
                    isLikedByMe={kit.isLikedByMe}
                    forSale={(kit as any).for_sale === true}
                  />
                ))}
                {loadingMore && Array.from({ length: pageSize }).map((_, i) => (
                  <FeedSkeleton key={`skeleton-more-${i}`} />
                ))}
              </div>
              {hasMore && (
                <div className="text-center pt-5">
                  <Button variant="outline" onClick={loadMoreFeed} disabled={loadingMore} className="touch-manipulation">
                    {loadingMore ? "Ładowanie..." : "Załaduj więcej"}
                  </Button>
                </div>
              )}
            </>
          ) : loadError ? (
            <div className="rounded-xl border bg-card p-8 text-center">
              <Shirt className="mx-auto h-12 w-12 text-muted-foreground/30" />
              <p className="mt-3 text-muted-foreground">Nie udało się załadować koszulek. Sprawdź połączenie i spróbuj ponownie.</p>
              <Button variant="outline" className="mt-4" onClick={retry} disabled={isRetrying}>
                {isRetrying ? "Ładowanie..." : "Spróbuj ponownie"}
              </Button>
            </div>
          ) : (
            <div className="rounded-xl border bg-card p-8 text-center">
              <Shirt className="mx-auto h-12 w-12 text-muted-foreground/30" />
              <p className="mt-3 text-muted-foreground">Brak koszulek do wyświetlenia</p>
            </div>
          )}

        </div>
      </div>
    </section>
  );
}